import { G, Svg, Text as SvgText } from '@svgdotjs/svg.js';
import { ClickInfo } from 'app/controls/image-cropper/drag-sensor';
import { StageConstants } from 'core/constants';
import { TextApplianceResult, TextEditingResult } from './text.controller';
import { TextControllerMethods } from './text.controller.methods';
import { isAutomaticWordBreak } from './text.layout';
import { FontDescription, renderedContent, Text, TextAlignment, TextChunk,
         TextChunkKind
       } from './text.model';

export interface TextInputLine {
  content: string;
  start: number;
}

interface TextInputGeometry {
  lines: Array<TextInputLine>;
  lineHeight: number;
  lineTop(lineIndex: number): number;
  columnX(lineIndex: number, column: number): number;
}

export interface TextInputChange {
  keptPrefixLength: number;
  inserted: string;
  keptSuffixLength: number;
}

export interface DisplayedText {
  model: Text;
  font: FontDescription;
}

export interface SvgTextInputHost {
  displayedModel(): DisplayedText | undefined;
  respondToInput(): Promise<void>;
  respondToEscape(): void;
}

interface TextSelectionListeners {
  begin(event: MouseEvent): void;
  drag(event: MouseEvent): void;
  end(): void;
}

export function lineColumnOf(
  lines: Array<TextInputLine>,
  index: number): { line: number; column: number }
{
  let line = 0;
  while (line < lines.length - 1 && lines[line + 1].start <= index) {
    line++;
  }

  return {
    line,
    column: Math.min(index - lines[line].start, lines[line].content.length),
  };
}

export function textInputLinesOf(text: Array<TextChunk>): Array<TextInputLine>
{
  const lines: Array<TextInputLine> = [];
  let line: TextInputLine = { content: '', start: 0 };
  let index = 0;

  for (const chunk of text) {
    if (chunk.breaksLine) {
      lines.push(line);
      index += isAutomaticWordBreak(chunk) ? 0 : 1;
      line = { content: '', start: index };
    }
    else if (chunk.kind === TextChunkKind.Word) {
      line.content += chunk.content || '';
      index += (chunk.content || '').length;
    }
    else if (chunk.kind === TextChunkKind.Whitespace) {
      line.content += ' ';
      index += (chunk.content || ' ').length;
    }
  }
  lines.push(line);

  return lines;
}

export function plaintextOf(text: Array<TextChunk>): string
{
  return text
    .map(chunk => chunk.kind === TextChunkKind.Word
        ? chunk.content || ''
        : chunk.kind === TextChunkKind.Whitespace
            ? (chunk.breaksLine ? '\n' : chunk.content || ' ')
            : '')
    .join('');
}

export function textInputChangeOf(
  previousValue: string, value: string): TextInputChange
{
  let keptPrefixLength = 0;
  while (keptPrefixLength < previousValue.length &&
         keptPrefixLength < value.length &&
         previousValue[keptPrefixLength] === value[keptPrefixLength]) {
    keptPrefixLength++;
  }
  let keptSuffixLength = 0;
  while (keptSuffixLength < previousValue.length - keptPrefixLength &&
         keptSuffixLength < value.length - keptPrefixLength &&
         previousValue[previousValue.length - 1 - keptSuffixLength] ===
             value[value.length - 1 - keptSuffixLength]) {
    keptSuffixLength++;
  }

  return {
    keptPrefixLength,
    inserted: value.slice(keptPrefixLength, value.length - keptSuffixLength),
    keptSuffixLength,
  };
}

export interface TextInputAppliance {
  partialResult: TextEditingResult;
  caret: number;
  completeRefusal?: TextEditingResult;
}

export function textInputApplianceOf(
  previousValue: string,
  value: string,
  resultFor: (value: string) => TextEditingResult): TextInputAppliance
{
  const { keptPrefixLength, inserted, keptSuffixLength } =
      textInputChangeOf(previousValue, value);
  const resultInserting = (count: number): TextEditingResult => resultFor(
      value.slice(0, keptPrefixLength) +
      inserted.slice(0, count) +
      value.slice(value.length - keptSuffixLength));
  const succeeds = (result: TextEditingResult): boolean =>
      result.type === TextApplianceResult.Success;

  const complete = resultInserting(inserted.length);
  if (succeeds(complete)) {
    return {
      partialResult: complete,
      caret: keptPrefixLength + inserted.length,
    };
  }

  let count = 0;
  let result = resultInserting(0);
  let upper = inserted.length - 1;
  while (count < upper) {
    const middle = Math.ceil((count + upper) / 2);
    const candidate = resultInserting(middle);
    if (succeeds(candidate)) {
      count = middle;
      result = candidate;
    }
    else {
      upper = middle - 1;
    }
  }

  return {
    partialResult: result,
    caret: keptPrefixLength + count,
    completeRefusal: complete,
  };
}

function textInputGeometryOf(
  model: Text,
  font: FontDescription,
  anchorXInSvg: number,
  heightInSvg: number): TextInputGeometry
{
  const lines = textInputLinesOf(model.text);

  const widthOf = (content: string): number =>
      TextControllerMethods.calculateStringWidth(
          renderedContent(content, model.effects), font.charDimensions) *
      model.fontSize / font.charDimensions.baseEM;

  const lineStartX = (line: string): number => {
    switch (model.horizontalAlignment) {
      case TextAlignment.Middle: return anchorXInSvg - widthOf(line) / 2;
      case TextAlignment.End: return anchorXInSvg - widthOf(line);
      default: return anchorXInSvg;
    }
  };

  const lineHeight = model.leading * model.fontSize;
  const emptySpace = heightInSvg - lines.length * lineHeight;
  const offset = (() => {
    switch (model.verticalAlignment) {
      case TextAlignment.Middle: return emptySpace / 2;
      case TextAlignment.End: return emptySpace;
      default: return 0;
    }
  })();

  return {
    lines,
    lineHeight,
    lineTop: (lineIndex: number) => offset + lineIndex * lineHeight,
    columnX: (lineIndex: number, column: number) =>
        lineStartX(lines[lineIndex].content) +
        widthOf(lines[lineIndex].content.slice(0, column)),
  };
}

export class SvgTextInput
{
  private readonly textarea: HTMLTextAreaElement;
  private cursor: G | undefined = undefined;
  private selectionAnchor: number = 0;
  private readonly selectionListeners: TextSelectionListeners = {
    begin: this.respondToSelectionBegin.bind(this),
    drag: this.respondToSelectionDrag.bind(this),
    end: this.respondToSelectionEnd.bind(this),
  };
  private readonly cursorListener: EventListener = () => this.applyCursor();
  private readonly textareaListeners: Record<string, EventListener> = {
    'input': () => this.host.respondToInput(),
    'keydown': this.respondToKey.bind(this),
    'keyup': this.cursorListener,
    'select': this.cursorListener,
    'selectionchange': this.cursorListener,
    'focus': this.cursorListener,
    'blur': this.cursorListener,
  };

  constructor(
    private wrapper: Svg,
    private text: SvgText,
    private surfaces: Array<Element>,
    private host: SvgTextInputHost,
    value: string)
  {
    const textarea = document.createElement('textarea');
    textarea.setAttribute('autocomplete', 'off');
    textarea.setAttribute('spellcheck', 'false');
    Object.assign(textarea.style, {
      position: 'fixed',
      opacity: '0',
      pointerEvents: 'none',
      resize: 'none',
      border: '0',
      padding: '0',
      fontSize: '16px',
    });
    textarea.value = value;

    for (const [type, listener] of Object.entries(this.textareaListeners)) {
      textarea.addEventListener(type, listener);
    }

    document.body.appendChild(textarea);
    this.textarea = textarea;

    this.surfaces.forEach(surface => surface.addEventListener(
        'mousedown', this.selectionListeners.begin));
  }

  public teardown(): void
  {
    this.respondToSelectionEnd();
    this.surfaces.forEach(surface => surface.removeEventListener(
        'mousedown', this.selectionListeners.begin));

    for (const [type, listener] of Object.entries(this.textareaListeners)) {
      this.textarea.removeEventListener(type, listener);
    }

    this.textarea.remove();
    this.applyCursor();
  }

  public get value(): string
  {
    return this.textarea.value;
  }

  public applyValue(value: string, caret: number): void
  {
    this.textarea.value = value;
    this.textarea.setSelectionRange(caret, caret);
    this.applyCursor();
  }

  public applyInitialSelection(
    click: ClickInfo | undefined, selectsAll: boolean): void
  {
    const index = click ? this.indexAt(click) : undefined;
    const length = this.textarea.value.length;

    if (selectsAll) {
      this.textarea.select();
    }
    else if (index !== undefined) {
      this.textarea.setSelectionRange(index, index);
    }
    else {
      this.textarea.setSelectionRange(length, length);
    }
  }

  public focus(): void
  {
    const box = this.wrapper.node.getBoundingClientRect();
    Object.assign(this.textarea.style, {
      left: `${box.left}px`,
      top: `${box.top}px`,
      width: `${box.width}px`,
      height: `${box.height}px`,
    });

    this.textarea.focus({ preventScroll: true });
    this.applyCursor();
  }

  private geometryOf(displayed: DisplayedText): TextInputGeometry
  {
    return textInputGeometryOf(
        displayed.model,
        displayed.font,
        Number(this.text.attr('x')) || 0,
        this.wrapper.height() as number);
  }

  public applyCursor(): void
  {
    if (this.cursor) {
      this.cursor.remove();
      this.cursor = undefined;
    }

    if (!this.textarea.isConnected ||
        document.activeElement !== this.textarea) {
      return;
    }
    const displayed = this.host.displayedModel();
    if (!displayed) {
      return;
    }
    const geometry = this.geometryOf(displayed);

    const cursor =
        new G().addClass(StageConstants.CLASS_NAME_TEXT_INPUT_CURSOR);
    cursor.insertBefore(this.text);
    this.cursor = cursor;

    const start = lineColumnOf(geometry.lines, this.textarea.selectionStart);
    const end = lineColumnOf(geometry.lines, this.textarea.selectionEnd);

    if (this.textarea.selectionStart === this.textarea.selectionEnd) {
      const x = geometry.columnX(end.line, end.column);
      const top = geometry.lineTop(end.line);
      cursor.line(x, top, x, top + geometry.lineHeight)
        .css('stroke', displayed.model.color);
      return;
    }

    for (let line = start.line; line <= end.line; line++) {
      const x = geometry.columnX(
          line, line === start.line ? start.column : 0);
      const width = geometry.columnX(
          line,
          line === end.line
            ? end.column
            : geometry.lines[line].content.length) - x;
      const lineBreakWidthLikeSpace =
          line < end.line ? geometry.lineHeight / 4 : 0;

      cursor.rect(width + lineBreakWidthLikeSpace, geometry.lineHeight)
        .move(x, geometry.lineTop(line));
    }
  }

  private indexAt(click: ClickInfo): number | undefined
  {
    const displayed = this.host.displayedModel();
    // Firefox ignores x and y of the nested svg wrapper in its screen CTM.
    const textToScreen =
        (this.text.node as SVGGraphicsElement).getScreenCTM();

    if (!displayed || !textToScreen) {
      return undefined;
    }
    const geometry = this.geometryOf(displayed);

    const point = new DOMPoint(click.clientX, click.clientY)
      .matrixTransform(textToScreen.inverse());

    const line = Math.min(
        geometry.lines.length - 1,
        Math.max(0, Math.floor(
            (point.y - geometry.lineTop(0)) / geometry.lineHeight)));

    const distanceAt = (column: number): number =>
        Math.abs(geometry.columnX(line, column) - point.x);
    const length = geometry.lines[line].content.length;
    let column = 0;
    while (column < length && distanceAt(column + 1) < distanceAt(column)) {
      column++;
    }

    return geometry.lines[line].start + column;
  }

  private respondToKey(event: KeyboardEvent): void
  {
    if (event.key === 'Escape') {
      this.host.respondToEscape();
    }
  }

  private respondToSelectionBegin(event: MouseEvent): void
  {
    const index = event.button === 0 ? this.indexAt(event) : undefined;

    if (index === undefined) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const textarea = this.textarea;
    const value = textarea.value;
    textarea.focus({ preventScroll: true });

    // The browser counts the clicks, including the one entering editing.
    const count = event.detail;

    if (count >= 3) {
      textarea.setSelectionRange(0, value.length);
    }
    else if (count === 2) {
      let wordStart = index;
      let wordEnd = index;
      while (wordStart > 0 && !/\s/.test(value[wordStart - 1])) {
        wordStart--;
      }
      while (wordEnd < value.length && !/\s/.test(value[wordEnd])) {
        wordEnd++;
      }
      textarea.setSelectionRange(wordStart, wordEnd);
    }
    else {
      this.selectionAnchor = event.shiftKey
        ? (textarea.selectionDirection === 'backward'
            ? textarea.selectionEnd
            : textarea.selectionStart)
        : index;
      this.applySelectionTo(index);

      window.addEventListener('mousemove', this.selectionListeners.drag);
      window.addEventListener('mouseup', this.selectionListeners.end);
    }

    this.applyCursor();
  }

  private respondToSelectionDrag(event: MouseEvent): void
  {
    const index = this.indexAt(event);

    if (index !== undefined) {
      this.applySelectionTo(index);
      this.applyCursor();
    }
  }

  private respondToSelectionEnd(): void
  {
    window.removeEventListener('mousemove', this.selectionListeners.drag);
    window.removeEventListener('mouseup', this.selectionListeners.end);
  }

  private applySelectionTo(index: number): void
  {
    const anchor = this.selectionAnchor;

    this.textarea.setSelectionRange(
        Math.min(anchor, index),
        Math.max(anchor, index),
        index < anchor ? 'backward' : 'forward');
  }
}
